import { reactive } from 'vue';

/**
 * LEVEL-UP ANNOUNCEMENT.
 *
 * A level was only ever mentioned on the fight outcome panel, so a level earned
 * from an errand, a raid, a quest or a tutorial step passed in silence — the bar
 * simply wrapped around (QA 86cb91hh1: "show a notification when you level up").
 *
 * The account level is the one number every faucet moves, and the profile is
 * refetched after all of them, so this watches the level itself rather than
 * asking each faucet to remember to announce. `observe()` is called with every
 * fresh profile read: it fires when the number goes UP, stays quiet on the first
 * read of a session (mounting is not a level-up), and stays quiet if the level
 * ever moves down (a reroll starts a new hero at 1).
 */

/** Per-level combat growth. MIRRORS `PER_LEVEL` in the backend's
 *  hero-progression.service.ts — change both together. */
export const PER_LEVEL_GROWTH = { attack: 2, defense: 1, magic: 1, maxHp: 10 } as const;

export interface LevelUpNotice {
  /** The level just reached. */
  level: number;
  /** How many levels were crossed at once (a big payout can cross two). */
  gained: number;
}

export const levelUp = reactive<{ notice: LevelUpNotice | null }>({ notice: null });

/** The last level we have seen; null until the first profile read lands. */
let seen: number | null = null;

/** Raise the banner for `level`, `gained` levels above where the hero was. */
export function announce(level: number, gained = 1): void {
  levelUp.notice = { level, gained: Math.max(1, gained) };
}

/** Drop the banner (auto-dismiss, click, or leaving the screen). */
export function clearNotice(): void {
  levelUp.notice = null;
}

/**
 * Feed every fresh account level through here. Returns true when it raised the
 * banner, so callers can tell a level-up happened without reading the state.
 */
export function observe(level: number | null | undefined): boolean {
  if (typeof level !== 'number' || !Number.isFinite(level)) return false;
  const previous = seen;
  seen = level;
  if (previous === null || level <= previous) return false;
  announce(level, level - previous);
  return true;
}

/** Forget the watched level — used when the session ends or the hero is reset,
 *  so the next profile read counts as a first read rather than a level-up. */
export function resetObserved(): void {
  seen = null;
  levelUp.notice = null;
}
